import { otto } from "@gr-exports/async/nexus";
import { withResolvers } from "@otto-ec/assets-core-utils/promise";
import { hashParams, searchParams } from "../data/params";
import type { StateManaged } from "../data/state.types";
import type { NavigationParameters, ParameterRecord } from "../data/params.types";
import { asyncScope, locationAssign } from "./utils";
import { sanitizeIncommingState, sanitizeState } from "../data/state";
import type { NavigationCurrent, NavigationResult, NavigationType } from "./navigation.types";

/*                                                            */
/*                                    */

const log = asyncScope.scope("navigation");

export const { current, currentHash, currentSearch, currentState, currentUrl, onNavigate } =
  otto.navigation;

/**
 *
 *
 */
export function getCurrentNavigation(
  fromUrl?: string | URL | null | undefined,
  hashFilter?: string[],
  searchFilter?: string[],
  state = sanitizeState(history.state, true),
): readonly [
  URL: URL,
  state: StateManaged,
  hash: NavigationParameters<ParameterRecord>,
  search: NavigationParameters<ParameterRecord>,
] {
  const url = new URL(fromUrl || location.href, window.location.origin);
  const hash = hashParams(url, hashFilter);
  const search = searchParams(url, searchFilter);

  return [url, state, hash, search] as const;
}

/**
 *
 *
 *
 *
 *
 *
 */
export const navTupleToObject = (
  data: ReturnType<typeof getCurrentNavigation>,
  info: unknown,
  type: NavigationType,
): NavigationCurrent => ({
  url: data[0],
  state: data[1],
  hash: data[2],
  search: data[3],
  info,
  type,
});

/**
 *
 *
 *
 *
 *
 *
 *
 *
 */
export function dispatchOnNavigate(
  info: unknown,
  cancelable: boolean,
  navigationType: NavigationType,
  to: ReturnType<typeof getCurrentNavigation>,
): [cancelled: boolean, info: unknown, complete: () => void] {
  let cancelInfo: unknown = undefined;

  /*                                                          */
  const completed = withResolvers<NavigationResult>();

  /*                                                              */
  /*                  */
  /*                                                           */
  /*                                                */
  /*                                  */
  /*                                           */
  /*                                                                     */
  /*   */
  /*                  */
  onNavigate.dispatch({
    info,
    cancelable,
    cancel: (reason) => (cancelable ? void (cancelInfo = reason) : undefined),
    onComplete: (handler) => void completed[0].then(handler),
    type: navigationType,
    from: { ...current.get()!, info, type: navigationType },
    destination: navTupleToObject(to, info, navigationType),
  });
  /**/
  /*  */

  /*                              */
  /*                                                     */

  const cancelled = cancelable && !!cancelInfo;
  return [cancelled, cancelInfo, () => completed[1]({ cancelled, info: cancelInfo })];
}

/**
 *
 *
 *
 *
 *
 */
export function updateCurrentNavigationSignals(
  info: unknown,
  navigationType: NavigationType,
  to: ReturnType<typeof getCurrentNavigation>,
): void {
  const [url, state, hash, search] = to;

  current.set(navTupleToObject(to, info, navigationType));
  currentUrl.set(url);
  currentState.set(state);
  currentHash.set(hash);
  currentSearch.set(search);
}

const navigationInfo = {
  info: undefined as unknown,
  complete: undefined as (() => void) | undefined,
};

/**
 *
 *
 *
 */
export const provideNavigationInfo = (info: unknown): void => {
  navigationInfo.info = info;
};

export function initNavigationEvents(): void {
  const type: NavigationType = "popstate";

  window.addEventListener("popstate", async (ev) => {
    log.info("incomming popstate event", ev.state);
    const nav = getCurrentNavigation();

    /*                                                */
    const [, , complete] = await dispatchOnNavigate(navigationInfo.info, false, type, nav);

    /*                                                          */
    updateCurrentNavigationSignals(navigationInfo.info, type, nav);
    navigationInfo.info = undefined;

    /*                            */
    complete();
  });
}

export const navigateCrossDocument = (url: URL, info: unknown): boolean => {
  const [cancelled, cancellationReason, complete] = dispatchOnNavigate(
    info,
    true,
    "document",
    getCurrentNavigation(url, undefined, undefined, sanitizeIncommingState(true, {}, {}, true)),
  );
  if (cancelled) return (log.info("Navigation was cancelled. Reason:", cancellationReason), false);

  log.debug("Performing document navigation to", url);
  locationAssign(url);
  complete();
  return true;
};
